import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { todayIST, formatIST } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Check, X, MapPin, ClockClockwise } from "@phosphor-icons/react";

export default function AttendanceReview() {
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState(todayIST());
  const [filter, setFilter] = useState("PENDING");

  async function load() {
    const { data } = await api.get(`/attendance/all?date=${date}`);
    setRows(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  async function decide(att, status) {
    try {
      await api.post(`/attendance/${att.id}/review`, { status, remarks: "" });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const filtered = rows.filter((r) => filter === "ALL" || (r.review_status || "PENDING") === filter);

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-2">Verification</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Attendance Photo Verification</h1>
        </div>
        <div className="flex gap-3">
          {["PENDING", "APPROVED", "REJECTED", "ALL"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
                    className={`text-xs uppercase tracking-widest px-3 py-2 border rounded-md transition-colors duration-200
                      ${filter === s ? "border-[#E50914] text-white bg-[#E50914]/10" : "border-[#2D2D35] text-zinc-400 hover:text-white"}`}>
              {s}
            </button>
          ))}
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 className="bg-[#16161C] border-[#2D2D35] h-10 w-44 [color-scheme:dark]" data-testid="review-date-picker" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="review-grid">
        {filtered.map((r) => {
          const inEv = r.events?.find((e) => e.type === "IN");
          const outEv = r.events?.find((e) => e.type === "OUT");
          return (
            <div key={r.id} className="bg-[#16161C] border border-[#2D2D35] rounded-md overflow-hidden">
              <div className="p-4 border-b border-[#2D2D35] flex items-center justify-between">
                <div>
                  <div className="text-white text-sm font-medium">{r.employee_name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{r.employee_id} · {r.shift}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{r.date}</div>
              </div>
              <div className="grid grid-cols-2 gap-1 bg-[#0D0D10]">
                {[inEv, outEv].map((ev, idx) => (
                  <div key={idx} className="aspect-square bg-[#0D0D10] relative">
                    {ev?.photo ? (
                      <img src={ev.photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[10px] text-zinc-600">
                        {idx === 0 ? "IN" : "OUT"} — pending
                      </div>
                    )}
                    <div className="absolute top-1.5 left-1.5 text-[9px] px-1.5 py-0.5 bg-black/70 text-white rounded">
                      {idx === 0 ? "IN" : "OUT"}
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 text-xs font-mono space-y-1.5 text-zinc-400">
                <div className="flex items-center gap-2"><ClockClockwise size={14} className="text-[#E50914]" /> IN: {formatIST(r.in_at, "full")} · OUT: {formatIST(r.out_at, "full")}</div>
                <div className="flex items-center gap-2"><MapPin size={14} className="text-[#E50914]" /> {inEv?.location_name || "—"}</div>
                <div className="text-[10px] text-zinc-500">
                  {inEv?.latitude?.toFixed?.(5)},{inEv?.longitude?.toFixed?.(5)} · accuracy {inEv?.accuracy || 0}m · distance {Math.round(inEv?.distance_m || 0)}m / {inEv?.allowed_radius || 0}m
                </div>
                {inEv?.verification_status && (
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className={`px-2 py-0.5 rounded font-semibold tracking-wide ${
                      inEv.verification_status === "VALID" ? "bg-[#22C55E]/15 text-[#22C55E]" :
                      inEv.verification_status === "LOCATION_MISMATCH" ? "bg-[#F59E0B]/15 text-[#F59E0B]" :
                      inEv.verification_status === "SUSPICIOUS" ? "bg-[#FF3B30]/15 text-[#FF3B30]" :
                      "bg-zinc-700/30 text-zinc-400"
                    }`}>GEO {inEv.verification_status.replaceAll("_"," ")}</span>
                  </div>
                )}
                <div className="text-[10px]">
                  Worked <span className="text-white">{r.computed?.worked_hours || 0}h</span> · Late <span className="text-[#F59E0B]">{r.computed?.late_minutes || 0}m</span> · {r.computed?.status || "—"}
                </div>
              </div>
              <div className="p-3 border-t border-[#2D2D35] flex gap-2">
                <Button size="sm" onClick={() => decide(r, "APPROVED")} data-testid={`approve-${r.id}`}
                        className="flex-1 bg-[#22C55E]/15 text-[#22C55E] hover:bg-[#22C55E]/25 border border-[#22C55E]/30">
                  <Check size={14} className="mr-1" /> Approve
                </Button>
                <Button size="sm" onClick={() => decide(r, "REJECTED")} data-testid={`reject-${r.id}`}
                        className="flex-1 bg-[#FF3B30]/15 text-[#FF3B30] hover:bg-[#FF3B30]/25 border border-[#FF3B30]/30">
                  <X size={14} className="mr-1" /> Reject
                </Button>
                <Button size="sm" onClick={() => decide(r, "CLARIFICATION")} variant="outline"
                        className="border-[#2D2D35] bg-transparent">
                  Clarify
                </Button>
              </div>
            </div>
          );
        })}
        {!filtered.length && <div className="text-center text-zinc-500 p-8 text-sm col-span-full">No items.</div>}
      </div>
    </div>
  );
}
